import React, { useCallback, useState } from 'react';
import moment from 'moment';

import { useExportReferralsMutation, useGetReferralsQuery } from 'api/main';
import { useDebounce } from 'utils/hooks';
import { parseFullName } from 'utils/helpers';
import { ORDER_BY, listingActionButtonStyle, sortDirection } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import {
    CustomTablePagination,
    EmptyTableBody,
    ListContainer,
    StyledTableRow,
    QuickSearchField,
    EnhancedTableHead,
    ActionButton,
} from 'components';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableRow,
    Typography,
} from '@mui/material';
import { referralSchemas } from 'schemas';

const tableHeaders = [
    { id: 1, isSortable: false, width: 25, label: texts.FUND_NAME },
    { id: 2, isSortable: false, width: 10, label: texts.SHARE_CATEGORY },
    { id: 3, isSortable: false, width: 15, label: texts.EMAIL },
    { id: 4, isSortable: false, width: 12, label: texts.DROITS_DE_ENTREE },
    { id: 5, isSortable: false, width: 13, label: texts.SENT_DATE },
    { id: 6, isSortable: false, width: 15, label: texts.CREATOR },
    {
        id: 'accountConfirmed',
        isSortable: true,
        width: 10,
        orderBy: ORDER_BY.accountConfirmed,
        label: texts.ENGAGED,
    },
];

const SentSubscriptions = () => {
    const [filters, setFilters] = useState({
        page: 1,
        itemsPerPage: 15,
        direction: sortDirection.DESC,
        orderBy: ORDER_BY.createdAt,
    });
    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 500);

    const {
        data: { referrals = [], lastPage } = { referrals: [], lastPage: 1 },
        isFetching: loading,
    } = useGetReferralsQuery(
        referralSchemas.getReferrals({ ...filters, token_email: debouncedKeyword })
    );
    const [exportReferrals, { isLoading }] = useExportReferralsMutation();

    const renderSentSubscription = useCallback(
        ({
            id,
            fundName,
            shareSubcategoryName,
            email,
            admissionFee,
            createdAt,
            accountConfirmed,
            createdBy,
        }) => (
            <StyledTableRow key={id}>
                <TableCell>{fundName || '-'}</TableCell>
                <TableCell>{shareSubcategoryName || '-'}</TableCell>
                <TableCell>{email || '-'}</TableCell>
                <TableCell>{admissionFee === null ? '-' : `${admissionFee}\xa0%`}</TableCell>
                <TableCell>{createdAt ? moment(createdAt).format('L') : '-'}</TableCell>
                <TableCell> {createdBy ? parseFullName(createdBy) : '-'} </TableCell>
                <TableCell>{!!accountConfirmed ? texts.OUI : texts.NON}</TableCell>
            </StyledTableRow>
        ),
        []
    );

    const handleSortSubscriptions = useCallback(
        (property, direction) => {
            setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
        },
        [setFilters]
    );

    const handlePageChange = useCallback(
        page => () => setFilters(oldValue => ({ ...oldValue, page })),
        [setFilters]
    );

    const handleReferralsListExport = useCallback(() => {
        exportReferrals(
            referralSchemas.getReferrals({ ...filters, token_email: debouncedKeyword })
        );
    }, [debouncedKeyword, filters, exportReferrals]);

    return (
        <ListContainer>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" align="left" gutterBottom>
                    {texts.SENT_SUBSCRIPTIONS}
                </Typography>
                {!loading && (
                    <ActionButton
                        variant="outlined"
                        sx={listingActionButtonStyle}
                        onClick={handleReferralsListExport}
                        disabled={isLoading}
                    >
                        {texts.EXPORTER_LISTE}
                    </ActionButton>
                )}
            </Box>
            <Box marginY={3} display="flex">
                <QuickSearchField
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    handleClear={() => setKeyword('')}
                />
            </Box>
            {!loading && debouncedKeyword && (
                <Typography variant="body2" gutterBottom>
                    {texts.RESULTS_FOR_KEYWORD(debouncedKeyword)}
                </Typography>
            )}
            <TableContainer>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {tableHeaders.map(th => (
                            <col key={th.id} width={`${th.width}%`} />
                        ))}
                    </colgroup>
                    <EnhancedTableHead
                        headCells={tableHeaders}
                        handleSort={handleSortSubscriptions}
                        direction={filters.direction}
                        orderBy={filters.orderBy}
                    />
                    {(!loading && referrals.length === 0) || loading ? (
                        <EmptyTableBody
                            colSpan={tableHeaders.length}
                            loading={!!loading}
                            message={
                                debouncedKeyword
                                    ? texts.NO_RESULTS_FOUND
                                    : texts.EMPTY_TABLE_DEFAULT
                            }
                        />
                    ) : (
                        <TableBody>{referrals.map(renderSentSubscription)}</TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={tableHeaders.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </ListContainer>
    );
};

export default SentSubscriptions;
